import type {
  IPriceRangeCalculationStrategy,
  PriceRangeCalculationInput,
  PriceRangeCalculationResult,
} from "./interfaces"
import { tickToPriceString as coreTickToPriceString } from "@/domains/manual-cl-core"
import { MAX_TICK, MIN_TICK } from "../../constants"
import { calculatePriceDifference, tryParseTickFromPrice } from "../../utils/price-to-tick-conversion"

/**
 * Custom Range Calculation Strategy
 * Handles calculations for custom price range positions
 * Supports both price and percentage input modes
 */
export class CustomRangeCalculationStrategy implements IPriceRangeCalculationStrategy {
  calculate(input: PriceRangeCalculationInput): PriceRangeCalculationResult {
    const { minPrice, maxPrice, inputMode, tickSpacing, token0, token1, currentPrice } = input

    // Validate required inputs
    if (!tickSpacing || !token0 || !token1) {
      return {
        ticks: [undefined, undefined],
        pricesAtTicks: ["", ""],
        priceDifferences: { min: undefined, max: undefined },
        ticksAtLimit: [false, false],
        minDisplayValue: "",
        maxDisplayValue: "",
        hasValidRange: false,
      }
    }

    // Convert prices to ticks
    const lowerTick = minPrice ? tryParseTickFromPrice(minPrice, token0, token1, tickSpacing) : undefined
    const upperTick = maxPrice ? tryParseTickFromPrice(maxPrice, token0, token1, tickSpacing) : undefined

    // Convert ticks back to prices for display. 8-decimal precision for range
    // boundaries; bit-exact via manual-cl-core so the round-trip with
    // tryParseTickFromPrice above cannot drift by 10^(dec0-dec1).
    const lowerPriceString =
      lowerTick !== undefined
        ? coreTickToPriceString({ tick: lowerTick, token0, token1, decimalPlaces: 8 })
        : ""
    const upperPriceString =
      upperTick !== undefined
        ? coreTickToPriceString({ tick: upperTick, token0, token1, decimalPlaces: 8 })
        : ""

    // Calculate price differences from current price
    const minPriceNum = minPrice ? parseFloat(minPrice) : undefined
    const maxPriceNum = maxPrice ? parseFloat(maxPrice) : undefined
    const priceDifferences = {
      min:
        minPriceNum !== undefined && currentPrice !== undefined
          ? calculatePriceDifference(minPriceNum, currentPrice)
          : undefined,
      max:
        maxPriceNum !== undefined && currentPrice !== undefined
          ? calculatePriceDifference(maxPriceNum, currentPrice)
          : undefined,
    }

    // Calculate display values based on input mode
    let minDisplayValue: string
    let maxDisplayValue: string

    if (inputMode === "percentage") {
      // In percentage mode, show percentage differences
      minDisplayValue = priceDifferences.min !== undefined ? priceDifferences.min.toFixed(2) : ""
      maxDisplayValue = priceDifferences.max !== undefined ? priceDifferences.max.toFixed(2) : ""
    } else {
      // In price mode, show the prices
      minDisplayValue = minPrice ?? ""
      maxDisplayValue = maxPrice ?? ""
    }

    // Check if ticks are at limits
    const ticksAtLimit: [boolean, boolean] = [
      lowerTick !== undefined && lowerTick <= MIN_TICK,
      upperTick !== undefined && upperTick >= MAX_TICK,
    ]

    // Validate range
    const hasValidRange = this.validateRange(lowerTick, upperTick, MIN_TICK, MAX_TICK)

    return {
      ticks: [lowerTick, upperTick],
      pricesAtTicks: [lowerPriceString, upperPriceString],
      priceDifferences,
      ticksAtLimit,
      minDisplayValue,
      maxDisplayValue,
      hasValidRange,
    }
  }

  /**
   * Validate that the tick range is valid
   */
  private validateRange(
    lowerTick: number | undefined,
    upperTick: number | undefined,
    minTick: number,
    maxTick: number,
  ): boolean {
    if (lowerTick === undefined || upperTick === undefined) {
      return false
    }

    // Check bounds
    if (lowerTick < minTick || upperTick > maxTick) {
      return false
    }

    // Check order
    if (lowerTick >= upperTick) {
      return false
    }

    return true
  }
}
